import { useCallback, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { Select, Spin } from "antd";
import { IconLineDown } from "@kalodata/kds-design-icons";
import { ProductOption } from "./ProductOption";
import type { AccountProduct, ProductPickerValue } from "./types";
import { toPickerValue } from "./types";
import type { UseAccountProductsResult } from "./useAccountProducts";

const LOAD_MORE_OFFSET = 32;
const SENTINEL_LOADING_VALUE = "__sentinel_loading__";
const SENTINEL_NO_MORE_VALUE = "__sentinel_no_more__";

interface EmptySelectProps {
  list: UseAccountProductsResult;
  disabled?: boolean;
  onChange: (next: ProductPickerValue) => void;
}

interface ProductOptionItem {
  value: string;
  label: string;
  product?: AccountProduct;
  sentinel?: "loading" | "no-more";
  disabled?: boolean;
}

export const EmptySelect = ({ list, disabled, onChange }: EmptySelectProps) => {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const options = useMemo<ProductOptionItem[]>(() => {
    const base: ProductOptionItem[] = list
      .filter(keyword)
      .filter((p) => Boolean(p.productId))
      .map((p) => ({
        value: p.productId ?? "",
        label: p.productTitle ?? "",
        product: p,
      }));
    if (list.loadingMore) {
      base.push({ value: SENTINEL_LOADING_VALUE, label: "", sentinel: "loading", disabled: true });
    } else if (!list.hasMore && base.length > 0) {
      base.push({ value: SENTINEL_NO_MORE_VALUE, label: "", sentinel: "no-more", disabled: true });
    }
    return base;
  }, [list, keyword]);

  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      if (
        list.hasMore &&
        !list.loadingMore &&
        target.scrollTop + target.clientHeight >= target.scrollHeight - LOAD_MORE_OFFSET
      ) {
        void list.loadMore();
      }
    },
    [list],
  );

  const showInitialSpin = list.loading && list.products.length === 0;

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled && nextOpen) return;
    setOpen(nextOpen);
    if (!nextOpen) setKeyword("");
    if (nextOpen) void list.loadInitial();
  };

  return (
    <Select<string, ProductOptionItem>
      open={disabled ? false : open}
      onOpenChange={handleOpenChange}
      showSearch={{
        searchValue: keyword,
        onSearch: setKeyword,
        optionFilterProp: "label",
        filterOption: (input, option) => {
          const k = input.trim().toLowerCase();
          if (option?.sentinel) return !k;
          return (option?.label ?? "").toLowerCase().includes(k);
        },
      }}
      allowClear={false}
      value={undefined}
      disabled={disabled}
      loading={list.loading}
      placeholder={t("Search or select a product")}
      suffixIcon={<IconLineDown size={20} className="text-kds-fg-soft" />}
      notFoundContent={
        <div className="flex items-center justify-center py-[12px] text-[12px] text-kds-fg-soft">
          {showInitialSpin ? <Spin size="small" /> : <Trans>No products match your search</Trans>}
        </div>
      }
      options={options}
      optionRender={(option) => {
        if (option.data.sentinel === "loading") {
          return (
            <div className="flex items-center justify-center py-[4px]">
              <Spin size="small" />
            </div>
          );
        }
        if (option.data.sentinel === "no-more") {
          return (
            <div className="flex items-center justify-center py-[4px] text-[12px] text-kds-fg-soft">
              <Trans>No more items</Trans>
            </div>
          );
        }
        return option.data.product ? <ProductOption product={option.data.product} keyword={keyword} /> : null;
      }}
      onPopupScroll={handlePopupScroll}
      onChange={(_value, option) => {
        const picked = Array.isArray(option) ? option[0] : option;
        if (!picked || picked.sentinel || !picked.product) return;
        onChange(toPickerValue(picked.product));
        setOpen(false);
        setKeyword("");
      }}
      classNames={{ popup: { root: "kds-product-picker-popup" }, input: "text-[14px] leading-[20px]" }}
      style={{ width: "100%" }}
      size="middle"
    />
  );
};
